import { useState, useEffect } from "react";
import { BaSeIcon } from "@base/react";
import moment from "moment";
import { toast } from "react-toastify";
import PostCreditButton from "./postCreditButton";
import { useNavigate } from "react-router-dom";
import {eventTag} from "../../services/utils";
import ToastContent from "../../components/toastContent";
import copyIcon from "../../assets/images/icons/copyIcon.svg";
import iconSuccess from "../../assets/images/icons/check-circle.png";

const borderGradients = [
  "linear-gradient(45deg, #9285F9 0%, #CD5BE8 100%)",
  "linear-gradient(45deg, #84F4BC 0%, #40BBFF 100%)",
  "linear-gradient(45deg, #FFB380 0%, #FFED69 100%)",
  "linear-gradient(45deg, #CD5BE8 0%, #FFABAB 100%)",
  "linear-gradient(45deg, #F4455A 0%, #FFABAB 100%)",
];

const PostCreditTable = ({
  plannings = [],
  monthsAmount = 12,
  startMonth,
  startYear,
  openedPlanning,
  refreshPlannings,
  eventGA,
}) => {
  const navigate = useNavigate();

  const [openedPlannings, setOpenedPlannings] = useState({});

  useEffect(() => {
    const initialState = plannings.reduce((acc, planning) => {
      acc[planning.id] = planning.id === openedPlanning;
      return acc;
    }, {});
    setOpenedPlannings(initialState);
  }, [plannings, openedPlanning]);

  const togglePlanning = (id) => {
    eventTag(eventGA?.name, eventGA?.category, eventGA?.action, "Ícone de abrir e fechar")
    setOpenedPlannings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const generateTimelineMonths = () => {
    const startDate = moment(`${startYear}-${String(startMonth).padStart(2, '0')}-01`);

    return Array.from({ length: monthsAmount }, (_, i) => {
      const date = startDate.clone().add(i, "months");
      const fullDate = date.format("YYYY-MM");
      const isCurrentMonth = date.isSame(moment(), "month");

      return {
        name: date.format("MMMM"),
        year: date.format("YYYY"),
        fullDate,
        isCurrentMonth,
        showYear: isCurrentMonth || date.month() === 0,
        firstMonth: i === 0,
      };
    });
  };

  const months = generateTimelineMonths();

  const getMonthData = (planning, monthFullDate) => {
    if (!planning?.months) return null;

    const target = planning.months.find(
      (m) =>
        m.year === parseInt(monthFullDate.split("-")[0]) &&
        m.month === parseInt(monthFullDate.split("-")[1])
    );

    return target || null;
  };

  const shouldShowButton = (monthData) => {
    if (!monthData) return false;
    const status = monthData.status;
    return (
      status === "pending" || status === "overdue" || status === "completed"
    );
  };

  const copyPostCreditId = async (postCreditId) => {
    try {
      await navigator.clipboard.writeText(postCreditId.toString());
      toast.success(
        <ToastContent
          title="Sucesso!"
          description="Texto copiado para a área de transferência!"
        />,
        {
          icon: () => <img src={iconSuccess} alt="icon" />,
          autoClose: 5000,
          pauseOnFocusLoss: false,
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="planning-table">
      <div className="plannings-list">
        {plannings.map((planning, index) => {
          const isOpened = openedPlannings[planning.id];

          return (
            <div
              key={planning.id}
              className={`planning-item ${!isOpened ? "closed" : ""}`}
            >
              <div
                className="planning-item-content"
                style={{
                  borderImage: `${
                    borderGradients[index % borderGradients.length]
                  } 1`,
                }}
              >
                {planning.isShared && isOpened && (
                  <span className="new-badge">NOVO!</span>
                )}
                <span
                  onClick={() =>{
                    navigate(`/planejamento/${planning.post_credit}/resultados`)
                    eventTag(eventGA?.name, eventGA?.category, eventGA?.action, `Pós crédito: ${planning?.title}`)
                  }}
                  className="planning-item-content-name"
                >
                  {planning.title}
                </span>
                {isOpened && (
                  <>
                    <span className="planning-item-content-date">
                      (Concluído em:{" "}
                      {moment(planning.finishedAt).format("DD/MM/YYYY")})
                    </span>
                    <div
                      className="planning-item-content-id"
                      onClick={() => copyPostCreditId(planning.id)}
                    >
                      ID: {planning.id}

                      <img
                        src={copyIcon}
                        alt="Copy Icon"
                        className="copy-icon"
                      />
                    </div>
                  </>
                )}
              </div>
              <BaSeIcon
                color="#8B99A7"
                description="abrir/fechar planejamento"
                name="angle-left-alt"
                size={isOpened ? 20 : 12}
                onClick={() => togglePlanning(planning.id)}
              />
            </div>
          );
        })}
      </div>
      <div className="planning-content">
        <div className="planning-months-grid">
          {months.map((month) => (
            <div
              key={month.fullDate}
              className={`planning-month ${
                month.isCurrentMonth ? "current-month" : ""
              }`}
            >
              {month.showYear && <span className="year">{month.year}</span>}
              <span className="month-name">{month.name}</span>
            </div>
          ))}
        </div>

        <div className="planning-content-cells">
          {plannings.map((planning) => {
            const isOpened = openedPlannings[planning.id];

            return (
              <div
                key={planning.id}
                className={`planning-row ${!isOpened ? "closed" : ""}`}
              >
                {months.map((month) => {
                  const monthData = getMonthData(planning, month.fullDate);

                  return (
                    <div
                      key={`${planning.id}-${month.fullDate}`}
                      className={`planning-cell ${
                        month.isCurrentMonth ? "current-month-cell" : ""
                      }`}
                    >
                      {isOpened && shouldShowButton(monthData) && (
                        <PostCreditButton
                          planningId={planning.id}
                          date={moment(month.fullDate).format("MMMM YYYY")}
                          fillDateLimit={moment(month.fullDate)
                            .add(1, "month")
                            .date(5)
                            .format("DD/MM/YYYY")}
                          closedAt={moment(monthData?.closed_at).format("DD/MM/YYYY")}
                          status={monthData.status}
                          monthId={monthData.id}
                          firstMonth={month.firstMonth}
                          refreshPlannings={refreshPlannings}
                          planningTitle={planning.title}
                          eventGA={eventGA}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PostCreditTable;
